import { unformat } from '@eventbrite/intl';
import isEmpty from 'lodash/isEmpty';
import { gettext } from '@eventbrite/i18n';
import { trackPayInSubmit, trackPayInSuccess } from '../eventAnalytics';
import {
    cardTypeForNumber,
    isValidCardNumberForCardType,
} from '@eventbrite/eds-utils';
import {
    shouldPerform3DS2Validation,
    getThreeDomainSecureNonce,
} from '../threeDomainSecurePayIn';
import { handleAnalyticsTokenOnError } from '../tokens';
import {
    PAYMENT_METHODS,
    PAYMENT_METHOD_TYPES_SUPPORTING_CREDIT_CARD_INSTRUMENT,
    PAYMENT_METHOD_TYPES_SUPPORTING_MAESTRO_BANCONTACT_INSTRUMENT,
    CREDIT_CARD_BASED_PAYMENT_METHODS,
} from '../../constants';
import { createNewVaultInstrument as createNewVaultInstrumentApi } from '../../api/users';
import {
    placePayIn as placePayInApi,
    formatCreditCardInstrument,
    getPayInPayload,
} from '../../api/payIn';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { getConstrainedPaymentMethods } from '../../selectors/tickets/pureSelectors';
import { getCurrency } from '../../selectors/event';
import {
    getUserId,
    isUserLoggedIn,
    getUserInstruments,
} from '../../selectors/user';
import {
    getAvailablePaymentMethods,
    getSelectedAmountToFund,
} from '../../selectors/payIn';
import { getIsNosSourceOfTruth } from '../../selectors/order/typedOrderSelectors';
import { notifyParentPayInComplete as notifyParentPayInCompleteAction } from '../notifyParent';
import {
    BUYER_FIRST_NAME_FIELD,
    BUYER_LAST_NAME_FIELD,
    BUYER_EMAIL_FIELD,
} from '../../containers/checkout/constants';
import {
    getPostMessageActionParamsFromState,
    handlePayInError,
    userIsStoringNewCreditCard,
    userSelectedStoredPaymentMethod,
    userSelectedNewCreditCardPaymentMethod,
    checkIfUserWasLoggedIn,
} from './utils';
import { CannedQuestionName } from '../../api/models/survey';

export const placePayInPromise = async ({
    dispatch,
    getState,
    paymentData = {},
    countryCode,
}) => {
    const state = getState();
    const { eventId } = getPostMessageActionParamsFromState(state);
    const currency = getCurrency(state);
    const isNOS = getIsNosSourceOfTruth(state);
    const body = getPayInPayload(
        countryCode,
        paymentData,
        currency,
        state.app.currencyFormat,
    );

    try {
        const placePayInResponse = await placePayInApi(eventId, body);

        const state = getState();

        // Check if a new user was created
        if (!isUserLoggedIn(state)) {
            checkIfUserWasLoggedIn()(dispatch, getState);
        }

        // Store the user's credit card to their "wallet" if they selected to save it.
        if (userIsStoringNewCreditCard(paymentData) && !isNOS) {
            const userId = getUserId(state);
            const currency = getCurrency(state);

            const { instrument, billingInformation } =
                formatCreditCardInstrument({
                    countryCode,
                    payment: paymentData.payment,
                    currency,
                    instrumentType: PAYMENT_METHODS.CREDIT,
                });

            createNewVaultInstrumentApi(userId, instrument, billingInformation);
        }

        dispatch(trackPayInSuccess());
        dispatch(notifyParentPayInCompleteAction());

        return placePayInResponse;
    } catch (error) {
        handleAnalyticsTokenOnError(dispatch, getState);
        handlePayInError(dispatch, error);
    }
};

/**
 * Saves and places the user's pay-in. This function will choose the correct flow and state updates
 * based on the user's selected payment method and information they have entered.
 */
/*eslint complexity: ["error", 25]*/
export const placePayIn = (formData) => (dispatch, getState) => {
    const state = getState();

    const availablePaymentMethods = getAvailablePaymentMethods(state);
    const { eventId, widgetParentUrl } =
        getPostMessageActionParamsFromState(state);
    const constrainedPaymentMethods = getConstrainedPaymentMethods(state);
    const eventVenue = state.event.venue;

    const countryCode =
        eventVenue.address && !isEmpty(eventVenue.address.country)
            ? eventVenue.address.country
            : state.app.envCountryCode;

    dispatch(trackPayInSubmit(getSelectedAmountToFund(state)));

    // First check if a CC number was used and if so is it one of the allowed types.
    const { payment: { creditCardNumber = null } = {} } = formData;

    if (creditCardNumber) {
        const cardType = cardTypeForNumber(creditCardNumber);
        const creditCardInstrument = availablePaymentMethods
            .filter(
                (method) =>
                    PAYMENT_METHOD_TYPES_SUPPORTING_CREDIT_CARD_INSTRUMENT[
                        method.type
                    ],
            )
            .flatMap((method) => method.instrumentTypes)
            .find(
                (instrument) =>
                    CREDIT_CARD_BASED_PAYMENT_METHODS[instrument.type],
            );

        const bancontactInstrument = availablePaymentMethods
            .filter(
                (method) =>
                    PAYMENT_METHOD_TYPES_SUPPORTING_MAESTRO_BANCONTACT_INSTRUMENT[
                        method.type
                    ],
            )
            .flatMap((method) => method.instrumentTypes)
            .find(
                (instrument) =>
                    instrument.type === PAYMENT_METHODS.MAESTRO_BANCONTACT,
            );

        if (creditCardInstrument) {
            const allowedCards = creditCardInstrument.variants;

            if (bancontactInstrument) {
                allowedCards.push(PAYMENT_METHODS.MAESTRO_BANCONTACT);
            }

            let cardNotAllowedMessage;

            if (
                !isEmpty(constrainedPaymentMethods) &&
                !constrainedPaymentMethods.includes(cardType)
            ) {
                cardNotAllowedMessage = gettext(
                    'Credit card does not match required card type for this ticket',
                );
            } else if (
                !allowedCards.includes(cardType) &&
                isValidCardNumberForCardType(
                    creditCardNumber,
                    cardType.toLowerCase(),
                )
            ) {
                cardNotAllowedMessage = gettext(
                    'The type of card you used is not supported.',
                );

                if (
                    allowedCards.includes('AMEX') &&
                    allowedCards.length === 1
                ) {
                    cardNotAllowedMessage = gettext(
                        'Only American Express cards are supported for this event.',
                    );
                }
            }

            if (cardNotAllowedMessage) {
                return {
                    saveAndPlacePromise: new Promise(() => {
                        throw new CustomSubmissionError(
                            {
                                payment: {
                                    creditCardNumber: cardNotAllowedMessage,
                                },
                                _error: gettext(
                                    'Please correct the highlighted fields below',
                                ),
                            },
                            {},
                        );
                    }).catch((error) => handlePayInError(dispatch, error)),
                };
            }
        }
    }

    const userInstruments = getUserInstruments(state);

    /**
     * When a stored payment method has been selected, tweak the formData to use the
     * USER_INSTRUMENT payment method and include the data for the selected instrument.
     */
    const paymentData = formData;

    if (isUserLoggedIn(state) && userSelectedStoredPaymentMethod(formData)) {
        paymentData.paymentMethod = PAYMENT_METHODS.USER_INSTRUMENT;
        paymentData.userInstrument = userInstruments.find(
            (instrument) => instrument.vaultId === formData.payment.vaultId,
        );
    }

    // Attach buyer info to payment method when using credit card payment method with a new card
    if (
        isUserLoggedIn(state) &&
        userSelectedNewCreditCardPaymentMethod(formData)
    ) {
        paymentData.payment[BUYER_FIRST_NAME_FIELD] =
            formData.buyer[CannedQuestionName.firstName];
        paymentData.payment[BUYER_LAST_NAME_FIELD] =
            formData.buyer[CannedQuestionName.lastName];
        paymentData.payment[BUYER_EMAIL_FIELD] =
            formData.buyer[CannedQuestionName.email];
    }

    const saveAndPlacePromiseArgs = {
        dispatch,
        state,
        getState,
        formData,
        paymentData,
        countryCode,
    };

    const currency = getCurrency(state);
    const amount = unformat(
        paymentData.payment.amount,
        state.app.currencyFormat,
    );
    const orderId = null;
    const userCountry = state.app.locale;

    const saveAndPlacePromise = shouldPerform3DS2Validation(
        saveAndPlacePromiseArgs,
    )
        ? getThreeDomainSecureNonce({
              countryCode,
              paymentData,
              currency,
              amount,
              orderId,
              eventId,
              userCountry,
          })
              .then((threeDomainSecureNonce) => {
                  if (!threeDomainSecureNonce) {
                      return saveAndPlacePromiseArgs;
                  }

                  const paymentDataBraintree = {
                      paymentMethod: PAYMENT_METHODS.NONCE_BRAINTREE,
                      payment: {
                          amount,
                          paypalBraintreeNonce: threeDomainSecureNonce,
                      },
                  };

                  return {
                      ...saveAndPlacePromiseArgs,
                      paymentData: paymentDataBraintree,
                  };
              })
              .then((placePayInPromiseArgs) =>
                  placePayInPromise(placePayInPromiseArgs),
              )
        : placePayInPromise(saveAndPlacePromiseArgs);

    return {
        dispatch,
        eventId,
        widgetParentUrl,
        formData,
        saveAndPlacePromise,
    };
};
